import { Modal, Typography, Space, Form, Input, Badge, Tooltip, Button } from "antd";
import React, { FC, useEffect, useRef, useState } from "react";
import moment from "moment";
import { useUser } from "../../context";
import { axiosInstant, endpoints } from "../../api";
import { showError } from "../../Notifications";
import { IRemark } from "../RecruitersPanel/helper";
import { CommentOutlined } from "@ant-design/icons";

interface Props {
  candidateId?: string;         // for manager–recruiter/project chat
  jobApplicationId?: string;    // for candidate–recruiter chat
  managerId?: string; // NEW: for recruiter-manager chat
  type: "recruiter-candidate" | "recruiter-manager";
  visible: boolean;
  onCancel: () => void;
}

export const RemarksPanel: FC<Props> = ({ candidateId, jobApplicationId, managerId, type, visible, onCancel }) => {
  const { user } = useUser();
  const [form] = Form.useForm();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [remarksList, setRemarksList] = useState<Array<IRemark>>([]);
  const [userMap, setUserMap] = useState<{ [userId: string]: any }>({});
  const [unreadCount, setUnreadCount] = useState<{ [id: string]: number }>({});

  const scrollToEnd = () => {
    scrollRef.current?.scrollTo({
      left: 0,
      top: scrollRef.current.scrollHeight || 0,
      behavior: "smooth",
    });
  };

  const getuserName = (name: string) => (user.name === name ? "You" : name);

  const fetchUsersForRemarks = async (remarks: Array<IRemark>) => {
    const uniqueIds = Array.from(new Set(remarks.map(r => r.senderId).filter(Boolean)));
    const newUserMap: { [userId: string]: any } = { ...userMap };
    for (const id of uniqueIds) {
      if (!id) continue;
      if (!newUserMap[id]) {
        try {
          const res = await axiosInstant.get(`/users/${id}`);
          newUserMap[id] = res.data;
        } catch {
          newUserMap[id] = { name: "Unknown", category: "unknown" };
        }
      }
    }
    setUserMap(newUserMap);
  };

  const getRemarks = async () => {
    let remarks: Array<IRemark> = [];
    if (type === "recruiter-candidate" && jobApplicationId) {
      const e = await axiosInstant.get(
        `/remarks/application/${jobApplicationId}?type=recruiter-candidate`
      );
      remarks = e?.data || [];
    } else if (type === "recruiter-manager" && jobApplicationId) {
      const e = await axiosInstant.get(`/remarks/application/${jobApplicationId}/manager`);
      remarks = e?.data || [];
    }
    setRemarksList(remarks);
    fetchUsersForRemarks(remarks);
  };

  const closeModal = () => {
    setLoading(false);
    onCancel();
  };

  const createRemark = async () => {
    setLoading(true);
    const { remark: value } = await form.validateFields();
    try {
      if (type === "recruiter-candidate" && jobApplicationId) {
        await axiosInstant.post(`/remarks/application/${jobApplicationId}`, {
          message: value,
          type: "recruiter-candidate",
        });
        await getRemarks();
      } else if (type === "recruiter-manager" && jobApplicationId) {
        await axiosInstant.post(`/remarks/application/${jobApplicationId}/manager`, {
          message: value,
        });
        await getRemarks();
      }
      form.resetFields();
    } catch (e) {
      showError((e as Error)?.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (candidateId || jobApplicationId) getRemarks();
  }, [candidateId, jobApplicationId]);

  useEffect(() => {
    setTimeout(scrollToEnd, 300);
  }, [remarksList]);

  // Opening a conversation has to clear both halves of "unread". The remarks are
  // what the per-application count reads; the chat_message notifications are what
  // the Jobs tab badge reads. Only the first was ever cleared, so the badge came
  // back unchanged every time the recruiter returned to the tab. Both are done
  // here because every panel - recruiter jobs, recruiter applicants, manager and
  // candidate - opens this same component.
  useEffect(() => {
    if (!user || !user._id || !jobApplicationId || !visible) return;
    axiosInstant.post('/remarks/mark-read', { userId: user._id, jobApplicationId });
    axiosInstant
      .patch(`/notifications/my/chat-read/${jobApplicationId}`, null, { params: { chatType: type } })
      .then(() => {
        // Let the open list views drop the badge without a reload.
        window.dispatchEvent(
          new CustomEvent('chatRead', { detail: { jobApplicationId, chatType: type } })
        );
      })
      .catch((err) => console.error('Failed to clear chat notifications:', err));
  }, [visible, user, jobApplicationId, type]);

  const getDisplayName = (remark: IRemark) => {
    if (remark.senderId === user._id) return "You";
    const sender = userMap[remark.senderId || ""];
    if (!sender) return remark.senderId || "Unknown";
    return sender.name || (sender.category ? sender.category.charAt(0).toUpperCase() + sender.category.slice(1) : "Unknown");
  };

  return (
    <Modal
      title="Applicant Remarks"
      centered
      destroyOnClose
      open={visible}
      confirmLoading={loading}
      okText="Create Comment"
      onOk={createRemark}
      cancelText="Close"
      onCancel={closeModal}
    >
      <div style={{ maxHeight: 600, overflow: "auto" }} ref={scrollRef}>
        {remarksList.map((remark) => (
          <div
            style={{
              padding: "20px 10px",
              borderBottom: "1px solid #f4f4f4",
            }}
            key={remark.createdAt}
          >
            <Space size="middle" style={{ marginBottom: 10 }}>
              <Typography style={{ fontSize: 16, fontWeight: "bold" }}>
                {getDisplayName(remark)}
              </Typography>
              <Typography style={{ fontSize: 14 }}>
                {moment(remark.createdAt).format("DD MMM, hh:mm A")}
              </Typography>
            </Space>
            <Typography>{remark.text}</Typography>
          </div>
        ))}
      </div>

      <div style={{ position: "relative", minHeight: 120 }}>
        <Form
          form={form}
          layout="vertical"
          style={{ position: "absolute", bottom: 0, width: "100%" }}
          preserve={false}
        >
          <Form.Item style={{ margin: "15px 0" }} name="remark">
            <Input.TextArea
              rows={3}
              placeholder="Comment (if any)"
              style={{ resize: "none" }}
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default RemarksPanel;
